/* Owns the generic pill. Every domain badge (severity, action, command status,
   global status) renders through it so the shapes stay identical. */

import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from './cn.js'

export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'warn' | 'danger'
export type BadgeVariant = 'solid' | 'soft' | 'outline'

export interface BadgeProps {
  children: ReactNode
  tone?: BadgeTone
  variant?: BadgeVariant
  size?: 'sm' | 'md'
  icon?: LucideIcon
  className?: string
  title?: string
  'data-testid'?: string
}

// Written out in full because Tailwind scans source text; a runtime-assembled
// class name would simply not exist in the generated stylesheet.
const TONES: Record<BadgeTone, Record<BadgeVariant, string>> = {
  neutral: {
    solid: 'bg-border-strong text-text',
    soft: 'bg-border-strong/30 text-text-muted',
    outline: 'border border-border-strong text-text-muted',
  },
  accent: {
    solid: 'bg-accent-solid text-accent-fg',
    soft: 'bg-accent/15 text-accent',
    outline: 'border border-accent text-accent',
  },
  ok: {
    solid: 'bg-ok text-surface',
    soft: 'bg-ok/15 text-ok',
    outline: 'border border-ok text-ok',
  },
  warn: {
    solid: 'bg-warn text-surface',
    soft: 'bg-warn/15 text-warn',
    outline: 'border border-warn text-warn',
  },
  danger: {
    solid: 'bg-danger text-accent-fg',
    soft: 'bg-danger/15 text-danger',
    outline: 'border border-danger text-danger',
  },
}

export function Badge({
  children,
  tone = 'neutral',
  variant = 'soft',
  size = 'sm',
  icon: Icon,
  className,
  title,
  'data-testid': testId,
}: BadgeProps): ReactNode {
  return (
    <span
      title={title}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1 rounded-full whitespace-nowrap font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
        TONES[tone][variant],
        className,
      )}
    >
      {Icon ? <Icon aria-hidden className="size-3 shrink-0" /> : null}
      {children}
    </span>
  )
}
